'use client';
import React, { useState, useRef, useEffect } from 'react';

export default function EmailSubscribeSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('subscribe-active');
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || status === 'loading') return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
        setErrorMsg(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  return (
    <section className="relative bg-[#060609] py-24 px-6 md:px-12 overflow-hidden">
      {/* Ambient glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.1) 0%, transparent 65%)', filter: 'blur(50px)' }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[200px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(236,72,153,0.07) 0%, transparent 65%)', filter: 'blur(30px)' }}
      />

      <div
        ref={sectionRef}
        className="subscribe-wrap relative z-10 max-w-2xl mx-auto text-center"
      >
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-violet-500/20 bg-violet-500/5 mb-6">
          <div className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-pulse" />
          <span className="font-sans text-xs text-violet-300 tracking-wide">Stay in the Loop</span>
        </div>

        {/* Headline */}
        <h2 className="font-sans font-bold text-3xl md:text-4xl text-white leading-tight mb-3">
          Get Early Access &amp;{' '}
          <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            Exclusive Updates
          </span>
        </h2>
        <p className="font-sans text-base text-white/40 mb-10 max-w-md mx-auto leading-relaxed">
          Be among the first to get AI UGC tips, workflow drops, and early access deals — straight to your inbox.
        </p>

        {status === 'success' ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.2), rgba(236,72,153,0.2))', border: '1px solid rgba(139,92,246,0.3)' }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M5 13L9 17L19 7" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="font-sans font-semibold text-lg text-white">You&apos;re in! 🎉</p>
            <p className="font-sans text-sm text-white/40">Check your inbox — exclusive content is on its way.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              className="flex-1 px-5 py-4 rounded-2xl font-sans text-sm text-white placeholder-white/25 outline-none focus:ring-1 focus:ring-violet-500/50 transition-all duration-200"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="relative flex-shrink-0 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl font-sans font-semibold text-sm text-white overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed transition-opacity duration-200"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7, #ec4899)' }}
            >
              {status === 'loading' ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Subscribing...</span>
                </>
              ) : (
                <>
                  <span>Subscribe Free</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8H13M9 4L13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </>
              )}
            </button>
          </form>
        )}

        {status === 'error' && (
          <p className="mt-3 font-sans text-sm text-red-400">{errorMsg}</p>
        )}

        {status !== 'success' && (
          <p className="mt-4 font-sans text-xs text-white/20">
            No spam, ever. Unsubscribe anytime.
          </p>
        )}
      </div>

      <style jsx>{`
        .subscribe-wrap {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1), transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .subscribe-active {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
}
